import { IReportIntrospection, ReportFilterEntry } from '@common/types';
import {
  Box,
  Button,
  Popover,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { isArray, isEmpty, isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { Select } from '@ui';
import { useReportStore } from '@store';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import { theme } from '@store';
import { FilterTypeMapper } from './FilterTypeMapper.tsx';
import { operatorLabels } from '../../../types.ts';
import { selectBooleanOptions } from '@common/types/constants.ts';
import {
  useBuildingsShortQuery,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  useProvidersShortQuery,
  useTeamsShortQuery,
  useUserListShortQuery,
} from '@common/cache';

const getStartAdornment = (operator?: string) => {
  return operator ? (
    <InputAdornment
      position='start'
      sx={{
        pointerEvents: 'none',
        flexShrink: 0,
        maxWidth: '120px',
      }}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          maxWidth: '120px',
        }}
      >
        {
          operatorLabels[
            operator as IReportIntrospection['filters'][0]['operators'][0]
          ]
        }
      </span>
    </InputAdornment>
  ) : undefined;
};

const getFormatedValue = (
  entry: ReportFilterEntry,
  options: { name: string; id: string }[],
): string => {
  const { value, operator, field } = entry;
  const typeOfValue = typeof value;

  if (operator === '$isnull')
    return (
      selectBooleanOptions.find(o => o.value.toString() === value.toString())
        ?.label ?? ''
    );

  if (
    field === 'assigned_user_id' ||
    field === 'assigned_team_id' ||
    field === 'care_navigator_id' ||
    field === 'organization_id' ||
    field === 'provider_id' ||
    field === 'building_id' ||
    field === 'partner_id'
  ) {
    switch (typeOfValue) {
      case 'string': {
        const userDetails = options.find(o => o.id === value);

        return userDetails?.name || value.toString();
      }
      case 'object': {
        const usersDetails = options.filter(o => value.includes(o.id));

        return usersDetails.map(i => i.name).join(', ');
      }
      default:
        return value.toString();
    }
  }

  return typeOfValue === 'string' ? value.toString() : value[0];
};

interface IFilterEntryProps {
  entry: ReportFilterEntry;
}

export const FilterEntry = ({ entry }: IFilterEntryProps) => {
  const { filters = [], setFilters } = useReportStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [value, setValue] = useState<string>('');

  const { data: userOptions = [], isPending: isUserOptionsLoading } =
    useUserListShortQuery({
      disabled: !['care_navigator_id', 'assigned_user_id'].includes(
        entry.field,
      ),
    });
  const { data: orgOptions = [], isPending: isOrgOptionsLoading } =
    useOrganizationsShortQuery();
  const { data: teamOptions = [], isLoading: isTeamOptionsLoading } =
    useTeamsShortQuery({});
  const { data: partnerOptions = [], isPending: isPartnerOptionsLoading } =
    usePartnerShortQuery();
  const { data: providerOptions = [], isPending: isProviderOptionsLoading } =
    useProvidersShortQuery({ disabled: entry.field !== 'provider_id' });
  const { data: buildingOptions = [], isPending: isBuildingOptionsLoading } =
    useBuildingsShortQuery({ disabled: entry.field !== 'building_id' });

  const handleSaveFilter = (newEntry: ReportFilterEntry) => {
    const entryIndex = filters.findIndex(o => o.field === entry.field);
    const updatedFilters = filters.map((o, index) =>
      entryIndex === index ? newEntry : o,
    );

    setFilters(updatedFilters);

    setAnchorEl(null);
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleRemoveEntry = () => {
    const updatedFilters = filters.filter(o => o.field !== entry.field);
    setFilters(updatedFilters);
    setAnchorEl(null);
  };

  useEffect(() => {
    switch (entry.field) {
      case 'assigned_user_id':
      case 'care_navigator_id': {
        return setValue(getFormatedValue(entry, userOptions));
      }
      case 'organization_id': {
        return setValue(getFormatedValue(entry, orgOptions));
      }
      case 'partner_id': {
        return setValue(getFormatedValue(entry, partnerOptions));
      }
      case 'provider_id': {
        return setValue(getFormatedValue(entry, providerOptions));
      }
      case 'building_id': {
        return setValue(getFormatedValue(entry, buildingOptions));
      }
      case 'assigned_team_id': {
        return setValue(getFormatedValue(entry, teamOptions));
      }
      case 'assigned_team': {
        const formatedValue = isArray(entry.value)
          ? entry.value.join(', ')
          : entry.value;
        return setValue(formatedValue);
      }
      default:
        return setValue(getFormatedValue(entry, []));
    }
  }, [
    isUserOptionsLoading,
    isOrgOptionsLoading,
    isPartnerOptionsLoading,
    isProviderOptionsLoading,
    isBuildingOptionsLoading,
    isTeamOptionsLoading,
    filters,
    entry,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        gap: '1rem',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <TextField
        label={entry?.label}
        value={value}
        slotProps={{
          htmlInput: {
            readOnly: true,
            style: {
              cursor: 'pointer',
            },
          },
          input: {
            startAdornment: entry?.operator
              ? getStartAdornment(entry?.operator)
              : null,
            endAdornment: (
              <InputAdornment position='end'>
                <ArrowDropDownIcon />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          bgcolor: theme.palette.common.white,
          cursor: 'pointer',
          '& .MuiInputBase-root': {
            cursor: 'pointer',
          },
        }}
        variant='outlined'
        fullWidth
        onClick={handleOpenPopover}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <IconButton onClick={handleRemoveEntry}>
          <DeleteIcon />
        </IconButton>
      </Box>

      {anchorEl && (
        <FilterPopover
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          handleApply={handleSaveFilter}
          initialState={entry}
        />
      )}
    </Box>
  );
};

const emptyEntry: ReportFilterEntry = {
  field: '',
  label: '',
  value: '',
  operator: '$eq',
};

interface IFilterPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  initialState?: ReportFilterEntry;
  handleApply: (newFilter: ReportFilterEntry) => void;
}

const getFirstAvailableOperator = (
  intro: Partial<IReportIntrospection>,
  field: string,
) => {
  const filterIntrospection = intro?.filters?.find(o => o.field === field);

  return filterIntrospection && filterIntrospection.operators.length > 0
    ? filterIntrospection.operators[0]
    : '';
};

export const FilterPopover = ({
  anchorEl,
  onClose,
  initialState = emptyEntry,
  handleApply,
}: IFilterPopoverProps) => {
  const [filter, setFilter] = useState<ReportFilterEntry>(initialState);
  const { introspection, filters } = useReportStore();
  const open = Boolean(anchorEl);

  const filterIntrospection = introspection?.filters?.find(
    o => o.field === filter.field,
  );

  const selectedFilterFields = (filters || []).map(o => o.field);
  const columnOptions = (introspection?.filters || [])
    .map(o => ({
      value: o.field,
      label: o.label,
    }))
    .filter(
      o =>
        !selectedFilterFields.includes(o.value) ||
        initialState.field === o.value,
    );

  const operatorOptions = (filterIntrospection?.operators ?? []).map(o => ({
    value: o,
    label: operatorLabels[o],
  }));

  const handleApplyAndClose = () => {
    handleApply(filter);
    onClose();
    setFilter(emptyEntry);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      slotProps={{ paper: { sx: { p: 3, width: 400, borderRadius: '0' } } }}
    >
      <Typography variant='h6' sx={{ mb: 2 }}>
        {isEqual(emptyEntry, initialState) ? 'Add' : 'Edit'} filter
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          flexDirection: 'column',
        }}
      >
        <Select
          name='column'
          clearable={false}
          options={columnOptions || []}
          value={filter.field}
          label='Column'
          required={false}
          onChange={event => {
            setFilter({
              ...filter,
              field: event.target.value,
              value: '',
              label:
                introspection?.filters?.find(
                  f => f.field === event.target.value,
                )?.label ?? '',
              operator: getFirstAvailableOperator(
                introspection as Partial<IReportIntrospection>,
                event.target.value,
              ),
            });
          }}
        />
        <Select
          name='operator'
          clearable={false}
          options={operatorOptions}
          value={filter.operator}
          label='Operator'
          required={false}
          onChange={event => {
            const operator = event.target.value;
            setFilter(prev => ({
              ...prev,
              operator,
              value: '',
              ...(operator === '$isnull' && { value: 'true' }),
            }));
          }}
          disabled={!filter.field}
        />

        <FilterTypeMapper
          entry={filter}
          introspection={filterIntrospection}
          handleOnChange={value => setFilter({ ...filter, value })}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant='outlined' onClick={onClose}>
          CANCEL
        </Button>
        <Button
          variant='contained'
          color='secondary'
          onClick={handleApplyAndClose}
          disabled={isEmpty(filter.value)}
        >
          APPLY
        </Button>
      </Box>
    </Popover>
  );
};
